import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, InputNumber, Modal, Popconfirm, Row, Select, Space, Table, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

/**
 * 27.10.2026: banka sözleşmesine bağlı kabin satırları (banka_sozlesme_kayit) — ekle / düzenle / sil.
 * Güncel 'sozlesme' akışında BankaSozlesmeFormDetay bunları yalnız listeliyordu; Sözleşmeler ekranından
 * BSK düzenlenemiyordu. Uçlar: /form/bsk-ekle, /form/bsk-guncelle/{id}, /form/bsk-sil/{id}.
 * acilacakId: fatura ekranı kısayolundan (?bsk=) gelinince o satırın düzenleme penceresi bir kez açılır.
 */
const { Text } = Typography;
const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
  .map((label, i) => ({ value: i + 1, label }));
const yetki = () => {
  const t = localStorage.getItem('auth_token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};
const tarihMi = (v) => v && !String(v).startsWith('0000');
const para = (v) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const paraGirdisi = {
  min: 0, style: { width: '100%' }, decimalSeparator: ',',
  formatter: (v) => (v === undefined || v === null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')),
  parser: (v) => (v || '').replace(/\./g, '').replace(',', '.'),
};

export default function BskSatirlari({ apiBase, sozlesmeId, satirlar, loading, onDegisti, darkMode, acilacakId }) {
  const [form] = Form.useForm();
  const [duzenlenen, setDuzenlenen] = useState(null); // null kapalı | {} yeni | satır
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [lokYukleniyor, setLokYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const yeni = duzenlenen && !duzenlenen.id;

  useEffect(() => {
    if (!yeni || lokasyonlar.length) return;
    setLokYukleniyor(true);
    axios.post(`${apiBase}/list/execute-query/sozlesme_lokasyonlar`, { params: {} }, yetki())
      .then((r) => setLokasyonlar((Array.isArray(r.data) ? r.data : []).map((l) => ({
        value: l.maptriksID, label: `${l.maptriksID} - ${l['Lokasyon Adı'] || ''}`,
      }))))
      .catch(() => message.error('Lokasyon listesi yüklenemedi'))
      .finally(() => setLokYukleniyor(false));
  }, [yeni, lokasyonlar.length, apiBase]);

  // Modal destroyOnClose: her açılışta Form yeniden kurulur, değerler initialValues'tan gelir
  const ac = (satir) => setDuzenlenen(satir || {});
  const ilkDegerler = duzenlenen && duzenlenen.id ? {
    ...duzenlenen, kiraBaslangic: tarihMi(duzenlenen.kiraBaslangic) ? dayjs(duzenlenen.kiraBaslangic) : null,
    odeme_ay: duzenlenen.odeme_ay || null,
  } : {};

  const acildi = useRef(false);
  useEffect(() => {
    if (acildi.current || !acilacakId) return;
    const satir = satirlar.find((r) => String(r.id) === String(acilacakId));
    if (!satir) return;
    acildi.current = true;
    ac(satir);
  }, [acilacakId, satirlar]); // eslint-disable-line react-hooks/exhaustive-deps

  const kaydet = async () => {
    const v = await form.validateFields();
    const govde = { ...v, kiraBaslangic: v.kiraBaslangic ? v.kiraBaslangic.format('YYYY-MM-DD') : null };
    setKaydediliyor(true);
    try {
      if (yeni) await axios.post(`${apiBase}/form/bsk-ekle`, { ...govde, sozlesme: sozlesmeId }, yetki());
      else await axios.put(`${apiBase}/form/bsk-guncelle/${duzenlenen.id}`, govde, yetki());
      message.success(yeni ? 'Kabin eklendi' : 'Kabin güncellendi');
      setDuzenlenen(null);
      onDegisti();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setKaydediliyor(false);
    }
  };

  const sil = async (r) => {
    try {
      await axios.delete(`${apiBase}/form/bsk-sil/${r.id}`, yetki());
      message.success('Kabin silindi');
      onDegisti();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <Card size="small"
      title={(
        <Space>
          <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>Sözleşmeye Bağlı Kabinler ({satirlar.length})</Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => ac(null)}>Kabin Ekle</Button>
        </Space>
      )}
      style={{ background: darkMode ? '#1b1d22' : '#fff', border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}` }}>
      <Table size="small" rowKey="id" loading={loading} dataSource={satirlar} pagination={false} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Bu sözleşmeye bağlı aktif kabin kaydı yok' }}
        columns={[
          { title: 'Kayıt', dataIndex: 'id', width: 70, sorter: (a, b) => a.id - b.id },
          { title: 'Lokasyon No', dataIndex: 'maptriksID', width: 100, sorter: (a, b) => (a.maptriksID || 0) - (b.maptriksID || 0) },
          { title: 'Lokasyon Adı', dataIndex: 'Name', ellipsis: true, sorter: (a, b) => String(a.Name || '').localeCompare(String(b.Name || ''), 'tr') },
          { title: 'Kabin', dataIndex: 'kabin', width: 70, sorter: (a, b) => (a.kabin || 0) - (b.kabin || 0) },
          { title: 'Kira Bedeli', dataIndex: 'kiraBedeli', width: 120, align: 'right', render: para, sorter: (a, b) => (a.kiraBedeli || 0) - (b.kiraBedeli || 0) },
          { title: 'Kabin Bedeli', dataIndex: 'kabinBedeli', width: 120, align: 'right', render: para },
          { title: 'Kira Başlangıç', dataIndex: 'kiraBaslangic', width: 110, render: (v) => (tarihMi(v) ? dayjs(v).format('DD.MM.YYYY') : '-') },
          { title: 'Ödeme Ayı', dataIndex: 'odeme_ay', width: 90, render: (v) => (v ? AYLAR[v - 1]?.label : '-') },
          {
            title: 'İşlem', key: 'islem', width: 90, fixed: 'right', render: (_, r) => (
              <Space size={4}>
                <Button size="small" icon={<EditOutlined />} onClick={() => ac(r)} aria-label="Düzenle" />
                <Popconfirm title="Kabin satırı silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => sil(r)}>
                  <Button size="small" danger icon={<DeleteOutlined />} aria-label="Sil" />
                </Popconfirm>
              </Space>
            ),
          },
        ]} />

      <Modal open={!!duzenlenen} title={yeni ? 'Kabin Ekle' : `Kabin Düzenle — #${duzenlenen?.id}`} onCancel={() => setDuzenlenen(null)}
        onOk={kaydet} confirmLoading={kaydediliyor} okText="Kaydet" cancelText="Vazgeç" destroyOnClose width={640}>
        <Form form={form} layout="vertical" requiredMark="optional" initialValues={ilkDegerler} preserve={false}>
          {yeni ? (
            <Form.Item name="lokasyon" label="Lokasyon" rules={[{ required: true, message: 'Lokasyon seçin' }]}>
              <Select showSearch optionFilterProp="label" options={lokasyonlar} loading={lokYukleniyor}
                placeholder="Lokasyon no ya da adıyla arayın" />
            </Form.Item>
          ) : (
            <Form.Item label="Lokasyon"><Text>{`${duzenlenen?.maptriksID || ''} - ${duzenlenen?.Name || ''}`}</Text></Form.Item>
          )}
          <Row gutter={12}>
            <Col xs={12} sm={8}>
              <Form.Item name="kabin" label="Kabin No" rules={[{ required: true, message: 'Kabin no' }]}>
                <InputNumber min={0} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="kiraBaslangic" label="Kira Başlangıç" rules={[{ required: true, message: 'Tarih seçin' }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="odeme_ay" label="Ödeme Ayı" tooltip="Boş bırakılırsa kira başlangıç ayı">
                <Select allowClear options={AYLAR} placeholder="Başlangıç ayı" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} sm={6}><Form.Item name="kiraBedeli" label="Kira Bedeli" rules={[{ required: true, message: 'Tutar' }]}><InputNumber {...paraGirdisi} /></Form.Item></Col>
            <Col xs={12} sm={6}><Form.Item name="kabinBedeli" label="Kabin Bedeli"><InputNumber {...paraGirdisi} /></Form.Item></Col>
            <Col xs={12} sm={6}><Form.Item name="elektrikBedeli" label="Elektrik Bedeli"><InputNumber {...paraGirdisi} /></Form.Item></Col>
            <Col xs={12} sm={6}><Form.Item name="ortak_alan_gideri" label="Ortak Alan"><InputNumber {...paraGirdisi} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
